/**
 * Der Aktiv-Zustand eines JTL-Datensatzes - EINE Einordnung, ein Ort.
 *
 * ============================== WARUM DAS EIN EIGENES MODUL IST ==============================
 *
 * `aktiv` ist in JTL kein Wahrheitswert, sondern `JtlAktiv = "Y" | "N" | null`
 * (`backend/src/jtl/werte.ts:5`, gefuellt aus `tlieferant.cAktiv` ueber `alsAktiv`). Der dritte
 * Zustand ist der heikle: `null` heisst UNBEKANNT, nicht "nein" und erst recht nicht "ja".
 *
 * Diese Unterscheidung ist im Projekt schon einmal verlorengegangen. Die Kopfkarte der
 * Lieferantenflaeche liess bis zur Fix-Runde 2 (2026-08-13) alles ausser "N" als aktiv gelten - bei
 * `aktiv: null` zeigte sie eine gruene Pille "AKTIV", waehrend die Stammdatenzeile zwei Zentimeter
 * darunter "–" zeigte. Seit dem 2026-08-13 gibt es einen ZWEITEN Ort, der denselben Zustand deutet
 * (die Marke an einem Suchtreffer, in ZWEI Suchfeldern) - und ein Vergleich auf "Y"/"N", der an drei
 * Stellen steht, ist genau die Bauform, aus der dieser Fehler ein zweites Mal entsteht.
 *
 * Deshalb: die Einordnung steht hier EINMAL, und jeder Anzeigeort leitet daraus ab, wie er sie malt.
 * Die Anzeige selbst (Pillenklasse, "ja"/"nein", Marke oder keine) bleibt Sache des jeweiligen
 * Ortes - sie unterscheidet sich wirklich; die Einordnung tut es nicht.
 */

/** Die drei Zustaende, die `JtlAktiv` kennt - benannt statt als Zeichenkette weitergereicht. */
export type AktivZustand = "aktiv" | "inaktiv" | "unbekannt";

/**
 * Ordnet einen rohen `aktiv`-Wert ein. Alles ausser den beiden bekannten Buchstaben ist
 * `"unbekannt"` - auch ein kuenftiger dritter Buchstabe, den JTL eines Tages einfuehrt. Er wird
 * damit SICHTBAR, statt still in einen der beiden bekannten Faelle zu fallen.
 */
export function aktivZustand(wert: unknown): AktivZustand {
  if (wert === "Y") return "aktiv";
  if (wert === "N") return "inaktiv";
  return "unbekannt";
}

/**
 * Der Wortlaut je Zustand, wie ihn Pille und Marke zeigen. "Status unbekannt" statt eines leeren
 * Textes: eine Marke, die nichts sagt, ist von "aktiv" nicht zu unterscheiden.
 */
export const AKTIV_BESCHRIFTUNG: Readonly<Record<AktivZustand, string>> = Object.freeze({
  aktiv: "aktiv",
  inaktiv: "inaktiv",
  unbekannt: "Status unbekannt",
});
